import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { 
  FiUsers, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiShield, 
  FiEye,
  FiEyeOff,
  FiSave,
  FiX
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';

const UserManagementContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const UsersTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 120px 100px 120px;
  gap: 16px;
  padding: 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 120px 100px 120px;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.role) {
      case 'Super Admin': return '#fee2e2';
      case 'Admin': return '#dbeafe';
      case 'Manager': return '#dcfce7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.role) {
      case 'Super Admin': return '#dc2626';
      case 'Admin': return '#2563eb';
      case 'Manager': return '#16a34a';
      default: return '#6b7280';
    }
  }};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.active ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.active ? '#16a34a' : '#dc2626'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &.danger:hover {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fecaca;
  }

  &.success:hover {
    background: #dcfce7;
    color: #16a34a;
    border-color: #bbf7d0;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: #3b82f6;
    color: white;

    &:hover {
      background: #2563eb;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 12px;
  color: #6b7280;
`;

function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_active: true,
    roles: []
  });

  useEffect(() => {
    if (!user || !user.permissions?.includes('user.read')) {
      toast.error('You do not have permission to access this page');
      navigate('/dashboard');
      return;
    }

    loadUsers();
    loadRoles();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await usersAPI.getRoles();
      setRoles(response.data.data);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      is_active: true,
      roles: []
    });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    
    // Convert role names back to role IDs
    const userRoleNames = user.roles ? user.roles.split(', ') : [];
    const userRoleIds = roles
      .filter(role => userRoleNames.includes(role.name))
      .map(role => role.id);
    
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      is_active: user.is_active,
      roles: userRoleIds
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = { ...formData };
      if (editingUser && !payload.password) {
        delete payload.password; // Don't update password if empty
      }

      const response = editingUser 
        ? await usersAPI.update(editingUser.id, payload)
        : await usersAPI.create(payload);

      toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
      setShowModal(false);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
      console.error('Error saving user:', error);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.message);
      console.error('Error deleting user:', error);
    }
  };

  const handleRoleChange = (roleId, checked) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, roleId]
        : prev.roles.filter(id => id !== roleId)
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <UserManagementContainer>
        <LoadingSpinner>Loading users...</LoadingSpinner>
      </UserManagementContainer>
    );
  }

  return (
    <UserManagementContainer>
      <PageHeader>
        <PageTitle>
          <FiUsers />
          User Management
        </PageTitle>
        {user?.permissions?.includes('user.create') && (
          <ActionButton onClick={handleCreateUser}>
            <FiPlus />
            Create User
          </ActionButton>
        )}
      </PageHeader>

      <UsersTable>
        <TableHeader>
          <div>Username</div>
          <div>Email</div>
          <div>Roles</div>
          <div>Status</div>
          <div>Created</div>
          <div>Actions</div>
        </TableHeader>

        {users.length === 0 ? (
          <EmptyState>
            <FiUsers size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3>No users found</h3>
            <p>Create your first user to get started</p>
          </EmptyState>
        ) : (
          users.map((tableUser) => (
            <TableRow key={tableUser.id}>
              <div style={{ fontWeight: '500' }}>{tableUser.username}</div>
              <div>{tableUser.email}</div>
              <div>
                {tableUser.roles ? (
                  tableUser.roles.split(', ').map((role, index) => (
                    <RoleBadge key={index} role={role}>
                      <FiShield size={12} />
                      {role}
                    </RoleBadge>
                  ))
                ) : (
                  <span style={{ color: '#9ca3af' }}>No roles</span>
                )}
              </div>
              <StatusBadge active={tableUser.is_active}>
                {tableUser.is_active ? <FiEye size={12} /> : <FiEyeOff size={12} />}
                {tableUser.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {formatDate(tableUser.created_at)}
              </div>
              <ActionButtons>
                <IconButton 
                  title="Edit User"
                  onClick={() => handleEditUser(tableUser)}
                >
                  <FiEdit size={16} />
                </IconButton>
                <IconButton 
                  title="Delete User"
                  className="danger"
                  onClick={() => handleDeleteUser(tableUser.id, tableUser.username)}
                >
                  <FiTrash2 size={16} />
                </IconButton>
              </ActionButtons>
            </TableRow>
          ))
        )}
      </UsersTable>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingUser ? 'Edit User' : 'Create User'}
              </ModalTitle>
              <IconButton onClick={() => setShowModal(false)}>
                <FiX size={16} />
              </IconButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Username</Label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Password {editingUser && '(leave empty to keep current)'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required={!editingUser}
                  minLength={6}
                />
              </FormGroup>

              <FormGroup>
                <Label>Roles</Label>
                <CheckboxGroup>
                  {roles.map((role) => (
                    <CheckboxItem key={role.id}>
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role.id)}
                        onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                      />
                      <span>{role.name}</span>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </FormGroup>

              <FormGroup>
                <CheckboxItem>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>Active</span>
                </CheckboxItem>
              </FormGroup>

              <ModalActions>
                <Button 
                  type="button" 
                  className="secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="primary">
                  <FiSave size={16} />
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </UserManagementContainer>
  );
}

export default UserManagement;
